import { GlobalState } from 'ha/types/store';
import { Language } from 'ha/i18n';
import { LinkData } from './types';
import transformHermesInfo from './utils/transformHermesInfo';
import transformHermesData from './utils/transformHermesData';

export const getError = (s: GlobalState) => {
  return s.hermes.error;
};

export const getData = (s: GlobalState) => {
  return s.hermes.data;
};

export const getInfo = (s: GlobalState) => {
  return s.hermes.info;
};

export const isCurrentPage = (s: GlobalState, pathname: string) => {
  const info = getInfo(s);

  if (!info) {
    return false;
  }
  const { alternateHrefs } = info.meta;

  return Object.keys(alternateHrefs).some(
    lang =>
      decodeURI(alternateHrefs[lang].toLowerCase()) ===
      decodeURI(pathname.toLowerCase()),
  );
};

export const getRoute = (state: GlobalState) => {
  const info = getInfo(state);

  return info && transformHermesInfo(info);
};

export const getRouteData = (state: GlobalState) => {
  const data = getData(state);
  const info = getInfo(state);

  return data && info && transformHermesData(data, info.type);
};

export const getAlternateHrefs = (s: GlobalState, pathname: string) => {
  const info = getInfo(s);

  if (!info || !isCurrentPage(s, pathname)) {
    return [];
  }

  const { alternateHrefs } = info.meta;

  return Object.keys(alternateHrefs).map(lang => ({
    code: lang,
    localisedLocation: alternateHrefs[lang],
  }));
};

export const getAlternateLangHref = (s: GlobalState, lang: Language) => {
  const info = getInfo(s);

  return info && info.meta.alternateHrefs[lang];
};

export const hasAlternateLangHref = (
  s: GlobalState,
  lang: Language,
  pathname: string,
) => {
  if (!isCurrentPage(s, pathname)) {
    return false;
  }

  return Boolean(getAlternateLangHref(s, lang));
};

export function getLinkData(s: GlobalState): LinkData[] | undefined {
  const info = getInfo(s);

  if (!info) {
    return undefined;
  }

  const canonicalLink = {
    rel: 'canonical' as 'canonical',
    href: info.meta.canonical,
  };
  const otherLinks: LinkData[] = info.availableLanguages
    ? info.availableLanguages.map(lang => ({
        // TODO: replace with "as const" after typescript 3.4
        rel: 'alternate' as 'alternate',
        hrefLang:
          info.meta.alternateHrefs[lang] === info.meta.canonical
            ? 'x-default'
            : lang,
        href: info.meta.alternateHrefs[lang],
      }))
    : [];

  return [canonicalLink, ...otherLinks];
}
